"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import React, { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Link } from "@/navigation";
import { cn } from "@/lib/utils";
import "swiper/css";
import "swiper/css/navigation";

// 图片路径常量
const IMAGE_BASE_PATH = "/image/new-version/home/part";

// 默认案例图片
const DEFAULT_CASE_IMAGES = [
	`${IMAGE_BASE_PATH}/cnc-machining-aerospace-complex-bracket.webp`,
	`${IMAGE_BASE_PATH}/injection-molding-automotive-intricate-interior-trim​.webp`,
	`${IMAGE_BASE_PATH}/metal-casting-large-marine-engine-block​.webp`,
	`${IMAGE_BASE_PATH}/sheet-metal-fabrication-high-precision-electronic-chassis​.webp`,
	`${IMAGE_BASE_PATH}/3d-printing-medical-customized-implant​.webp`,
	`${IMAGE_BASE_PATH}/gear-machining-high-sapeed-ev-transmission-gear​.webp`,
];

// 案例数量
const CASE_COUNT = 6;

// 组件 Props 接口
interface PrecisionSolutionsProps {
	// 翻译命名空间
	translationNamespace?: string;
	// 自定义案例图片数组（按顺序覆盖默认图片）
	caseImages?: string[];
	className?: string;
}

export default function PrecisionSolutions({
	translationNamespace = "precisionSolutions",
	caseImages,
	className,
}: PrecisionSolutionsProps) {
	const t = useTranslations(translationNamespace);

	// 使用传入的图片或默认图片
	const images = caseImages || DEFAULT_CASE_IMAGES;
	const swiperRef = useRef<SwiperType | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	const handleSlideChange = (swiper: SwiperType) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	return (
		<section className={cn("bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				{/* 标题区域 */}
				<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("description")}</p>
				</div>

				{/* Swiper 轮播区域 */}
				<div className="relative">
					{/* 桌面端：两侧箭头 */}
					<button
						onClick={() => swiperRef.current?.slidePrev()}
						disabled={isBeginning}
						className={`absolute left-0 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center transition-all duration-300 md:flex lg:-left-10 xl:-left-12 ${
							isBeginning ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100 hover:opacity-70"
						}`}
						aria-label="Previous slide"
					>
						<CaretLeft size={32} weight="bold" className="text-themePrimary400" />
					</button>

					<button
						onClick={() => swiperRef.current?.slideNext()}
						disabled={isEnd}
						className={`absolute right-0 top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center transition-all duration-300 md:flex lg:-right-10 xl:-right-12 ${
							isEnd ? "cursor-not-allowed opacity-30" : "cursor-pointer opacity-100 hover:opacity-70"
						}`}
						aria-label="Next slide"
					>
						<CaretRight size={32} weight="bold" className="text-themePrimary400" />
					</button>

					<Swiper
						modules={[Navigation, Autoplay]}
						spaceBetween={24}
						slidesPerView={1}
						onSwiper={(swiper) => {
							swiperRef.current = swiper;
						}}
						onSlideChange={handleSlideChange}
						className="overflow-hidden"
						autoHeight
					>
						{Array.from({ length: CASE_COUNT }, (_, index) => {
							const caseNum = index + 1;
							return (
								<SwiperSlide key={caseNum}>
									<article className="overflow-hidden border-4 border-[#e5e5e5] bg-white p-4 transition-shadow duration-300 sm:border-[6px] sm:px-6 sm:py-10 md:border-[8px] md:px-8 md:py-12 lg:px-10 lg:py-[70px]">
										<div className="flex flex-col gap-6 sm:gap-8 md:gap-10 lg:flex-row">
											{/* 左侧图片区域 */}
											<div className="relative aspect-[4/3] w-full overflow-hidden rounded-lg lg:aspect-auto lg:min-h-[400px] lg:w-[45%]">
												<Image
													src={images[index]}
													alt={t(`case${caseNum}Title`)}
													fill
													className="object-cover"
													sizes="(max-width: 1024px) 100vw, 45vw"
												/>
											</div>

											{/* 右侧内容区域 */}
											<div className="flex flex-1 flex-col">
												{/* 案例标题 */}
												<h3 className="mb-5 text-lg font-bold text-black sm:mb-6 sm:text-xl md:text-2xl">
													{t(`case${caseNum}Title`)}
												</h3>

												{/* 核心技术挑战 */}
												<div className="mb-4 sm:mb-6">
													<h4 className="mb-2 text-base font-semibold text-[#5a8f29] sm:mb-3 sm:text-lg">
														{t("coreChallenges")}
													</h4>
													<div className="space-y-2 text-sm sm:text-base md:grid md:grid-cols-[220px_1fr] md:gap-x-6 md:space-y-0">
														{[1, 2, 3].map((challengeNum) => {
															const labelKey = `case${caseNum}ChallengeLabel${challengeNum}`;
															const valueKey = `case${caseNum}ChallengeValue${challengeNum}`;
															const label = t.has(labelKey) ? t(labelKey) : "";
															const value = t.has(valueKey) ? t(valueKey) : "";
															// 如果标签和值都为空，则不渲染
															if (!label && !value) return null;
															return (
																<React.Fragment key={`challenge-${challengeNum}`}>
																	<div className="font-semibold text-gray-900 md:py-1">{label}</div>
																	<div className="text-gray-900 md:py-1">{value}</div>
																</React.Fragment>
															);
														})}
													</div>
												</div>

												{/* 我们的解决方案 */}
												<div className="mb-5 sm:mb-8">
													<h4 className="mb-2 text-base font-semibold text-[#5a8f29] sm:mb-3 sm:text-lg">
														{t("ourSolution")}
													</h4>
													<div className="space-y-3 text-sm sm:text-base md:grid md:grid-cols-[220px_1fr] md:gap-x-6 md:space-y-0">
														{[1, 2, 3].map((solutionNum) => {
															const labelKey = `case${caseNum}SolutionLabel${solutionNum}`;
															const valueKey = `case${caseNum}SolutionValue${solutionNum}`;
															const label = t.has(labelKey) ? t(labelKey) : "";
															const value = t.has(valueKey) ? t(valueKey) : "";
															// 如果标签和值都为空，则不渲染
															if (!label && !value) return null;
															return (
																<React.Fragment key={`solution-${solutionNum}`}>
																	<div className="font-semibold text-gray-900 md:py-1">{label}</div>
																	<div className="mb-2 text-gray-900 md:mb-0 md:py-1">{value}</div>
																</React.Fragment>
															);
														})}
													</div>
												</div>

												{/* 按钮区域 */}
												<div className="flex flex-wrap items-center gap-4 sm:gap-5">
													<Link
														href="/upload"
														className="inline-flex items-center justify-center rounded-md bg-themePrimary400 px-5 py-2.5 text-sm font-medium text-white transition-colors duration-200 hover:bg-opacity-80 hover:!text-white sm:px-6 sm:py-3 sm:text-base"
													>
														{t("getInstantQuote")}
													</Link>
													<Link
														href="/cases"
														className="hover:text-mainColor inline-flex items-center gap-1 text-sm font-medium text-themePrimary400 transition-colors duration-200 hover:underline sm:text-base"
													>
														{t("learnMore")}
														<svg
															className="h-3.5 w-3.5"
															fill="none"
															stroke="currentColor"
															viewBox="0 0 24 24"
														>
															<path
																strokeLinecap="round"
																strokeLinejoin="round"
																strokeWidth={2}
																d="M7 17L17 7M17 7H7M17 7v10"
															/>
														</svg>
													</Link>
												</div>
											</div>
										</div>
									</article>
								</SwiperSlide>
							);
						})}
					</Swiper>

					{/* 移动端：底部导航按钮 */}
					<div className="mt-4 flex items-center justify-center gap-4 md:hidden">
						<button
							onClick={() => swiperRef.current?.slidePrev()}
							disabled={isBeginning}
							className={`group flex h-10 w-10 items-center justify-center rounded-full border-2 border-themePrimary400 transition-all duration-300 ${
								isBeginning
									? "cursor-not-allowed opacity-30"
									: "cursor-pointer hover:bg-themePrimary400 hover:text-white"
							}`}
							aria-label="Previous slide"
						>
							<CaretLeft size={20} weight="bold" className="text-themePrimary400 group-hover:text-white" />
						</button>

						{/* 分页指示器 */}
						<div className="flex items-center gap-2">
							{Array.from({ length: CASE_COUNT }, (_, idx) => (
								<button
									key={idx}
									onClick={() => swiperRef.current?.slideTo(idx)}
									className={`h-2 rounded-full transition-all duration-300 ${
										swiperRef.current?.activeIndex === idx || (!swiperRef.current && idx === 0)
											? "w-6 bg-themePrimary400"
											: "w-2 bg-gray-300 hover:bg-gray-400"
									}`}
									aria-label={`Go to slide ${idx + 1}`}
								/>
							))}
						</div>

						<button
							onClick={() => swiperRef.current?.slideNext()}
							disabled={isEnd}
							className={`group flex h-10 w-10 items-center justify-center rounded-full border-2 border-themePrimary400 transition-all duration-300 ${
								isEnd
									? "cursor-not-allowed opacity-30"
									: "cursor-pointer hover:bg-themePrimary400 hover:text-white"
							}`}
							aria-label="Next slide"
						>
							<CaretRight size={20} weight="bold" className="text-themePrimary400 group-hover:text-white" />
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}
